import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import type { RegistrationAnswers, RegistrationFormField } from './useRegistrationAnswers';

export type ConditionOperation = 'equals' | 'notEquals' | 'ageUnder' | 'ageAtLeast';

export interface Condition {
  field: string;
  operation: ConditionOperation | string;
  value: string | number;
}

export interface FieldConditionRule {
  effect: 'show' | 'require';
  all: Condition[];
}

/**
 * A field can carry a rule: an effect, and conditions that must all hold for it to
 * apply. Mirrors DigitalTestCenter\Registration\Config\FieldCondition — the server applies
 * the same rule to decide whether the field is validated at all, so the two have to
 * agree case for case.
 */
export function useFieldConditions(
  fields: MaybeRefOrGetter<RegistrationFormField[]>,
  answers: MaybeRefOrGetter<RegistrationAnswers>,
) {
  /** Answers can be booleans, numbers or strings, so both sides are flattened. */
  const normalize = (value: unknown): string => {
    if (typeof value === 'boolean') {
      return value ? '1' : '0';
    }

    return value === null || value === undefined ? '' : String(value);
  };

  /**
   * Null while the age is unknown: the birthday is only composed once day, month
   * and year are all filled in, and an unknown age satisfies no age comparison.
   */
  const ageInYears = (birthday: unknown): number | null => {
    if (!birthday) {
      return null;
    }

    const date = birthday instanceof Date ? birthday : new Date(birthday as string);

    if (Number.isNaN(date.getTime()) || date > new Date()) {
      return null;
    }

    const now = new Date();
    let age = now.getFullYear() - date.getFullYear();
    const monthDiff = now.getMonth() - date.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
      age--;
    }

    return age;
  };

  const conditionHolds = ({ field, operation, value }: Condition): boolean => {
    const answer = toValue(answers)[field];

    switch (operation) {
      case 'equals':
        return normalize(answer) === normalize(value);
      case 'notEquals':
        return normalize(answer) !== normalize(value);
      case 'ageUnder':
      case 'ageAtLeast': {
        const age = ageInYears(answer);

        if (age === null) {
          return false;
        }

        return operation === 'ageUnder' ? age < Number(value) : age >= Number(value);
      }
      default:
        // Fail open, so a rule this client is too old to understand does not hide a field.
        return true;
    }
  };

  const conditionsHold = (field: RegistrationFormField): boolean =>
    !field.condition?.all || field.condition.all.every(conditionHolds);

  const isVisible = (field: RegistrationFormField): boolean =>
    field.condition?.effect !== 'show' || conditionsHold(field);

  const isRequired = (field: RegistrationFormField): boolean =>
    Boolean(field.templateOptions?.required)
    || (field.condition?.effect === 'require' && conditionsHold(field));

  /**
   * Filtered here rather than with a v-if on the v-for element, because in Vue 3
   * v-if is evaluated first and would not see the loop variable.
   */
  const visibleFields = computed<RegistrationFormField[]>(() => toValue(fields).filter(isVisible));

  return { visibleFields, isVisible, isRequired, conditionsHold };
}
